import {useEffect, useState} from 'react';
import {useParams, useSearchParams} from "react-router";
import {useReportStore} from "../useReportStore";
import {useInitUrlParams} from "../useInitUrlParams";
import {getReportById, ensureReportSearchQuery} from "../../../firebase/firestore/reports";
import {textToQuery} from "../../../utils/api/segments";
import useSourcesExplorerStore from "../reportEditorPage/leftPanel/components/useSourcesExplorerStore";

/**
 * Resolve the search query when a report loads.
 * Prefers explicit URL query; else stored defaultQuery; else self-generates from the topic.
 * When the report has no query, no defaultQuery, AND no topic (e.g. older imported reports), asks
 * the backend to self-heal: derive a topic from title + content, generate a query, persist it.
 * The report title is never used as a seed (it is the report name, not a search query).
 */
async function resolveSearchQuery(reportData, queryParam, reportId) {
    if (queryParam !== null) {
        return queryParam;
    }
    if (reportData.status === 'approved') {
        return '';
    }
    const storedDefault = (reportData.defaultQuery || '').trim();
    if (storedDefault) {
        return storedDefault;
    }
    // Self-generate a query when no explicit ?query= and no stored defaultQuery exist, derived
    // strictly from the persisted topic. The report title is intentionally NOT used as a seed:
    // the title is a human-readable report name (often unrelated to the search intent) and using
    // it as the query clobbers the auto-generated query with the report name in the Sources box.
    const seed = (reportData.topic || '').trim();
    if (seed) {
        try {
            const {query} = await textToQuery(seed);
            return (query || '').trim();
        } catch (error) {
            console.warn('textToQuery failed while loading report search query', error);
            return '';
        }
    }
    // No query, no defaultQuery, and no topic — back-fill from the report's content server-side.
    // This only fires when everything is empty, so reports that already have a query are untouched.
    try {
        const result = await ensureReportSearchQuery(reportId);
        return (result?.defaultQuery || '').trim();
    } catch (error) {
        console.warn('ensure-search-query failed while loading report', error);
        return '';
    }
}

/**
 * Custom hook that handles loading a report from dash-api.
 * Also manages the browser tab title and initializes search query from defaultQuery.
 *
 * Search query must be re-synced on each report load: Zustand keeps global state and
 * useInitUrlParams only reads the URL once per session, so opening another report
 * would otherwise keep the previous report's query until remount/reopen.
 *
 * @param {Object} user - The current user object
 * @returns {Object} - { isLoading, notFound }
 */
export const useReportLoader = (user) => {
    const {reportId} = useParams();
    const [searchParams] = useSearchParams();
    const setReport = useReportStore(s => s.setReport);
    const setQuery = useReportStore(s => s.setQuery);
    const setUnifiedSearchMode = useReportStore(s => s.setUnifiedSearchMode);
    const clearSearchResults = useReportStore(s => s.clearSearchResults);
    const currentReportId = useReportStore(s => s.id);
    const [isLoading, setIsLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [accessDenied, setAccessDenied] = useState(false);

    // Initialize URL params to Zustand state once
    useInitUrlParams();

    useEffect(() => {
        let mounted = true;
        setNotFound(false);
        setAccessDenied(false);
        setIsLoading(true);

        const loadReport = async () => {
            try {
                const reportData = await getReportById(reportId);
                if (!mounted) return;

                if (!reportData) {
                    setNotFound(true);
                    return;
                }

                const queryParam = searchParams.get('query');
                const resolvedQuery = await resolveSearchQuery(reportData, queryParam, reportId);

                // Reset cached search state so returning to a report re-runs search with the
                // resolved query (same query string would otherwise skip fetch via lastSearchKey).
                clearSearchResults();
                setQuery(resolvedQuery);

                // M6A: the unified Sources-panel search reads its input from the sources explorer
                // store, not useReportStore.query. Seed it here so the auto-generated topic query
                // (from report creation / ?query=) is visible and auto-runs in the Sources search
                // box — restoring the pre-consolidation behavior. Map ?aiSearch → hybrid (AI) mode.
                const explorer = useSourcesExplorerStore.getState();
                explorer.clearSearch();
                explorer.setInputValue(resolvedQuery);
                explorer.setDebouncedQuery((resolvedQuery || '').trim());
                if (searchParams.has('aiSearch')) {
                    setUnifiedSearchMode('hybrid');
                }

                setReport(reportData);
            } catch (error) {
                console.error('Error fetching report data:', error);
                if (mounted && error?.status === 403) {
                    setAccessDenied(true);
                }
            } finally {
                if (mounted) setIsLoading(false);
            }
        };

        if (reportId) loadReport();

        return () => {
            mounted = false;
        };
        // searchParams are read when this effect runs for a given reportId (navigate); omitting
        // searchParams from deps avoids re-loading the report when unrelated URL params change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportId, setReport, setQuery, setUnifiedSearchMode, clearSearchResults, user]);

    return {isLoading, notFound, accessDenied, reportId, currentReportId};
};
